import { SvgDefs, Box, Cyl, Pill, Hex, Arr, Zone, Badge, Label, Part, COLORS, type ColorKey } from './svg-primitives'

/** Step-through diagrams take the set of parts the current step lights (see `FlowStepper`). */
interface FlowProps {
  lit?: ReadonlySet<string> | null
}

export function DeploymentDiagram() {
  const id = 'dep'
  return (
    <svg viewBox='0 0 1120 700' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}>
      <SvgDefs id={id} />
      <Label x={190} y={138} text='PRESENTATION' size={10} weight={700} color='#7c3aed' />
      <Label x={620} y={138} text='APPLICATION + PERSISTENCE' size={10} weight={700} color='#e11d48' />
      <Zone x={420} y={148} w={400} h={470} title='VPC · 2 public + 2 private subnets · 2 AZs' bgColor='#eff6ff' borderColor='#93c5fd' />
      <Box x={460} y={18} w={200} h={66} title='Browser' subtitle='React 19 SPA client' color='neutral' id={id} />
      <Box x={55} y={170} w={270} h={76} title='CloudFront' subtitle='CDN · PriceClass_100 · ACM cert' color='frontend' id={id} />
      <Box x={80} y={340} w={220} h={76} title='Amazon S3 (private)' subtitle='React static build · OAC' color='frontend' id={id} />
      <Pill x={460} y={180} w={310} h={58} title='Application Load Balancer' subtitle='Public HTTPS :443 · ACM cert' color='network' id={id} />
      <Box x={485} y={315} w={260} h={82} title='ECS Fargate' subtitle='NestJS API · auto-scaling · private' color='api' id={id} />
      <Cyl x={515} y={478} w={200} h={82} title='PostgreSQL 16' subtitle='RDS · encrypted · Multi-AZ' color='db' id={id} />
      <Box x={880} y={218} w={200} h={66} title='AWS Cognito' subtitle='User pool · JWT issuance' color='auth' id={id} />
      <Box x={880} y={350} w={200} h={66} title='Secrets Manager' subtitle='DB URL · JWT secret' color='mock' id={id} />
      <Arr x1={560} y1={84} x2={190} y2={170} label='HTTPS' id={id} />
      <Arr x1={560} y1={84} x2={615} y2={180} label='Bearer JWT' id={id} />
      <Arr x1={190} y1={246} x2={190} y2={340} label='Origin Access' id={id} />
      <Arr x1={615} y1={238} x2={615} y2={315} id={id} />
      <Arr x1={615} y1={397} x2={615} y2={478} label='Prisma' id={id} />
      <Arr x1={745} y1={345} x2={880} y2={260} dashed label='auth proxy' id={id} />
      <Arr x1={745} y1={370} x2={880} y2={383} dashed label='inject' id={id} />
      <Badge x={825} y={207} text='ALB SG' color='#b45309' />
      <Badge x={825} y={352} text='ECS SG' color='#e11d48' />
      <Badge x={825} y={515} text='RDS SG' color='#2563eb' />
      <Badge x={560} y={662} text='Provisioned by Terraform / OpenTofu' color='#4338ca' />
    </svg>
  )
}

export function AwsTopologyDiagram() {
  const id = 'aws'
  return (
    <svg viewBox='0 0 1100 740' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}>
      <SvgDefs id={id} />
      <Box x={30} y={30} w={250} h={66} title='CloudFront' subtitle='app.{env}.smart-cgt.com' color='frontend' id={id} />
      <Box x={30} y={120} w={250} h={66} title='Amazon S3 (private)' subtitle='React static build' color='frontend' id={id} />
      <Arr x1={155} y1={96} x2={155} y2={120} id={id} />

      <Zone x={340} y={20} w={720} h={670} title='VPC · ONE PER ENVIRONMENT · 10.x.0.0/16' bgColor='#f8fafc' borderColor='#94a3b8' />
      <Label x={530} y={56} text='AZ-a' size={10} weight={600} color='#64748b' />
      <Label x={850} y={56} text='AZ-b' size={10} weight={600} color='#64748b' />

      <Zone x={370} y={72} w={660} h={155} title='PUBLIC SUBNETS' bgColor='#fef3c7' borderColor='#fcd34d' titleColor='#92400e' />
      <Pill x={400} y={108} w={240} h={50} title='ALB (AZ-a)' subtitle='HTTPS :443' color='network' id={id} />
      <Pill x={700} y={108} w={240} h={50} title='ALB (AZ-b)' subtitle='HTTPS :443' color='network' id={id} />
      <Box x={400} y={172} w={180} h={40} title='NAT Gateway' color='network' id={id} />

      <Zone x={370} y={255} w={660} h={165} title='PRIVATE APP SUBNETS' bgColor='#dcfce7' borderColor='#86efac' titleColor='#166534' />
      <Box x={400} y={295} w={240} h={76} title='ECS Fargate (AZ-a)' subtitle='NestJS API · :3000' color='api' id={id} />
      <Box x={700} y={295} w={240} h={76} title='ECS Fargate (AZ-b)' subtitle='NestJS API · :3000' color='api' id={id} />
      <Badge x={555} y={395} text='Auto-scaling 1–10 tasks' color='#16a34a' />

      <Zone x={370} y={450} w={660} h={160} title='PRIVATE DATA SUBNETS' bgColor='#f3e8ff' borderColor='#c084fc' titleColor='#6b21a8' />
      <Cyl x={420} y={490} w={200} h={80} title='RDS Primary' subtitle='PostgreSQL 16 · encrypted' color='db' id={id} />
      <Cyl x={720} y={490} w={200} h={80} title='RDS Standby' subtitle='Multi-AZ replica' color='db' id={id} />
      <Arr x1={620} y1={530} x2={720} y2={530} dashed label='sync replica' id={id} />

      <Arr x1={520} y1={158} x2={520} y2={295} id={id} />
      <Arr x1={820} y1={158} x2={820} y2={295} id={id} />
      <Arr x1={520} y1={371} x2={520} y2={490} id={id} />
      <Arr x1={820} y1={371} x2={820} y2={490} id={id} />
      <Arr x1={280} y1={63} x2={400} y2={133} label='API calls' id={id} />

      <Badge x={1050} y={133} text='SG: public :443' color='#b45309' />
      <Badge x={1050} y={333} text='SG: from ALB only' color='#16a34a' />
      <Badge x={1050} y={530} text='SG: from ECS only' color='#6b21a8' />
      <Badge x={700} y={715} text='Security groups chained: ALB → ECS → RDS' color='#64748b' />
    </svg>
  )
}

export function ConnectionFlowDiagram({ lit }: FlowProps = {}) {
  const id = 'cf'
  const F = 'system-ui, -apple-system, sans-serif'
  const steps = [
    { n: '1', t: 'User action', s: 'Click, submit or edit in the UI' },
    { n: '2', t: 'React component', s: 'Handles the event' },
    { n: '3', t: 'TanStack mutation', s: 'Optimistic mutation fired' },
    { n: '4', t: 'authFetch', s: 'Attaches Bearer + X-Tenant-Id' },
    { n: '5', t: 'NestJS REST', s: 'Hits the /api controller' },
    { n: '6', t: 'PostgreSQL', s: 'Read or write via Prisma' },
    { n: '7', t: 'Query invalidation', s: 'TanStack cache cleared' },
    { n: '8', t: 'Re-render', s: 'Screen updates with fresh state' },
  ]
  return (
    <svg viewBox='0 0 1000 620' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}>
      <SvgDefs id={id} />
      <Part k='browser' lit={lit}><Box x={40} y={20} w={260} h={85} title='Browser — React 19 SPA' subtitle='Pages · TanStack Query · authFetch' color='frontend' id={id} /></Part>
      <Part k='api' lit={lit}><Box x={370} y={20} w={260} h={85} title='NestJS Backend :3000' subtitle='/api REST · /ws Socket.IO' color='api' id={id} /></Part>
      <Part k='db' lit={lit}><Cyl x={700} y={22} w={200} h={82} title='PostgreSQL 16' subtitle='RDS · Prisma' color='db' id={id} /></Part>
      <Part k='cognito' lit={lit}><Box x={700} y={140} w={200} h={58} title='AWS Cognito' subtitle='JWT issuance' color='auth' id={id} /></Part>

      <line x1={170} y1={105} x2={170} y2={470} stroke='#e2e8f0' strokeWidth={1.5} strokeDasharray='4 3' />
      <line x1={500} y1={105} x2={500} y2={470} stroke='#e2e8f0' strokeWidth={1.5} strokeDasharray='4 3' />
      <line x1={800} y1={104} x2={800} y2={400} stroke='#e2e8f0' strokeWidth={1.5} strokeDasharray='4 3' />

      <Part k='signin' lit={lit}><Arr x1={500} y1={155} x2={700} y2={169} dashed label='sign-in / refresh' id={id} /></Part>
      <Part k='req' lit={lit}><Arr x1={190} y1={230} x2={480} y2={230} label='Bearer JWT + X-Tenant-Id' id={id} /></Part>
      <Part k='prisma' lit={lit}>
        <Arr x1={520} y1={310} x2={780} y2={310} label='Prisma ORM' id={id} />
        <Arr x1={780} y1={350} x2={520} y2={350} id={id} />
      </Part>
      <Part k='res' lit={lit}><Arr x1={480} y1={400} x2={190} y2={400} label='JSON response' id={id} /></Part>

      <Part k='req' lit={lit}>
        <rect x={65} y={215} width={28} height={28} rx={14} fill='#7c3aed' opacity={0.12} />
        <text x={79} y={230} textAnchor='middle' dominantBaseline='middle' fontFamily={F} fontSize={11} fontWeight={700} fill='#7c3aed'>4</text>
      </Part>
      <Part k='n5' lit={lit}>
        <rect x={395} y={295} width={28} height={28} rx={14} fill='#e11d48' opacity={0.12} />
        <text x={409} y={310} textAnchor='middle' dominantBaseline='middle' fontFamily={F} fontSize={11} fontWeight={700} fill='#e11d48'>5</text>
      </Part>
      <Part k='n6' lit={lit}>
        <rect x={660} y={295} width={28} height={28} rx={14} fill='#2563eb' opacity={0.12} />
        <text x={674} y={310} textAnchor='middle' dominantBaseline='middle' fontFamily={F} fontSize={11} fontWeight={700} fill='#2563eb'>6</text>
      </Part>

      <Zone x={20} y={490} w={960} h={110} title='USER ACTION TO RE-RENDERED SCREEN' bgColor='#f8fafc' borderColor='#e2e8f0' titleColor='#334155' />
      {steps.map((st, i) => {
        const col = i % 4
        const row = Math.floor(i / 4)
        const sx = 50 + col * 240
        const sy = 525 + row * 38
        return (
          <Part key={st.n} k={`s${st.n}`} lit={lit}>
            <circle cx={sx} cy={sy} r={11} fill='#7c3aed' opacity={0.12} />
            <text x={sx} y={sy + 1} textAnchor='middle' dominantBaseline='middle' fontFamily={F} fontSize={10} fontWeight={700} fill='#7c3aed'>{st.n}</text>
            <text x={sx + 18} y={sy - 4} dominantBaseline='middle' fontFamily={F} fontSize={11} fontWeight={600} fill='#1e293b'>{st.t}</text>
            <text x={sx + 18} y={sy + 10} dominantBaseline='middle' fontFamily={F} fontSize={9} fill='#64748b'>{st.s}</text>
          </Part>
        )
      })}
    </svg>
  )
}

export function PatientJourneyDiagram() {
  const id = 'pj'
  const stages: { label: string; sub: string; color: ColorKey }[] = [
    { label: 'Chart', sub: 'Patient intake', color: 'frontend' },
    { label: 'Transplant', sub: 'Procedure plan', color: 'api' },
    { label: 'Donor', sub: 'Donor eval', color: 'partner' },
    { label: 'CPO', sub: 'Work order', color: 'network' },
    { label: 'Collection', sub: 'Cell harvest', color: 'mllp' },
    { label: 'Processing', sub: 'Manufacturing', color: 'auth' },
    { label: 'Release Review', sub: 'QA review', color: 'ci' },
    { label: 'Distribution', sub: 'Final delivery', color: 'success' },
  ]
  const bw = 125, bh = 72, gap = 22
  const total = stages.length * bw + (stages.length - 1) * gap
  const sx = (1240 - total) / 2
  const by = 85
  return (
    <svg viewBox='0 0 1240 240' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}>
      <SvgDefs id={id} />
      <Label x={620} y={26} text='THE CGT PATIENT JOURNEY' size={14} weight={700} color='#1e293b' />
      <Label x={620} y={48} text='Eight ordered form types — each unlocked by its predecessor' size={11} weight={400} color='#64748b' />
      {stages.map((s, i) => {
        const x = sx + i * (bw + gap)
        return (
          <g key={s.label}>
            <Label x={x + bw / 2} y={by - 14} text={String(i + 1).padStart(2, '0')} size={10} weight={700} color='#7c3aed' />
            <Box x={x} y={by} w={bw} h={bh} title={s.label} subtitle={s.sub} color={s.color} id={id} />
            {i < stages.length - 1 && (
              <Arr x1={x + bw + 2} y1={by + bh / 2} x2={x + bw + gap - 2} y2={by + bh / 2} id={id} />
            )}
          </g>
        )
      })}
      <Label x={620} y={210} text='CgtFormType in src/types/cgt.ts · status logic in src/lib/journey.ts' size={9.5} weight={400} color='#94a3b8' />
    </svg>
  )
}

export function Hl7IntegrationDiagram({ lit }: FlowProps = {}) {
  const id = 'hl7'
  return (
    <svg viewBox='0 0 1100 580' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}>
      <SvgDefs id={id} />
      <Label x={550} y={25} text='HL7 v2 INTEGRATION PATH' size={14} weight={700} color='#1e293b' />

      <Zone x={20} y={55} w={280} h={320} title='CUSTOMER' bgColor='#f0fdf4' borderColor='#86efac' titleColor='#166534' />
      <Part k='emr' lit={lit}><Box x={45} y={95} w={230} h={80} title='EMR (Epic)' subtitle='Beacon · Beaker · HL7 v2' color='partner' id={id} /></Part>
      <Part k='vpn-endpoint' lit={lit}><Box x={55} y={210} w={210} h={56} title='VPN Endpoint' subtitle='Customer-side gateway' color='partner' id={id} /></Part>
      <Part k='emr-vpn' lit={lit}><Arr x1={160} y1={175} x2={160} y2={210} id={id} /></Part>

      <Part k='tunnel' lit={lit}><Hex x={340} y={195} w={180} h={70} title='IPsec IKEv2' subtitle='Encrypted tunnel' color='network' id={id} /></Part>
      <Part k='mllp-hop' lit={lit}><Arr x1={265} y1={238} x2={340} y2={230} label='MLLP' id={id} /></Part>

      <Zone x={570} y={55} w={500} h={320} title='AWS CLOUD · VPC' bgColor='#eff6ff' borderColor='#93c5fd' />
      <Part k='gateway' lit={lit}><Box x={600} y={95} w={250} h={56} title='VPN Gateway' subtitle='Terminates tunnel, routes to VPC' color='network' id={id} /></Part>
      <Part k='datalayer' lit={lit}><Box x={600} y={190} w={270} h={85} title='MLLP Data Layer' subtitle='ADT :6001 · ORM :6002 · ORU :6003 · API :8080' color='mllp' id={id} /></Part>
      <Part k='api' lit={lit}><Box x={600} y={305} w={270} h={56} title='Smart-CGT API' subtitle='NestJS · loopback :8080' color='api' id={id} /></Part>

      <Part k='tunnel-gw' lit={lit}><Arr x1={520} y1={230} x2={600} y2={123} id={id} /></Part>
      <Part k='gw-dl' lit={lit}><Arr x1={725} y1={151} x2={725} y2={190} id={id} /></Part>
      <Part k='loopback' lit={lit}><Arr x1={735} y1={275} x2={735} y2={305} dashed label='loopback' id={id} /></Part>

      <Part k='types' lit={lit}><Badge x={430} y={165} text='ADT · ORM · ORU' color='#0891b2' /></Part>
      <Part k='ack' lit={lit}><Badge x={430} y={275} text='ACK (MSA)' color='#16a34a' /></Part>
      <Part k='ack-arrow' lit={lit}><Arr x1={600} y1={260} x2={520} y2={260} dashed id={id} /></Part>

      <Part k='mgmt' lit={lit}>
        <Zone x={20} y={420} w={1050} h={115} title='MANAGEMENT & OBSERVABILITY' bgColor='#f8fafc' borderColor='#e2e8f0' />
        <Box x={50} y={458} w={210} h={55} title='CloudWatch' subtitle='Logs + alarms' color='ci' id={id} />
        <Box x={290} y={458} w={190} h={55} title='SNS' subtitle='Alert notifications' color='ci' id={id} />
        <Box x={510} y={458} w={220} h={55} title='Secrets Manager' subtitle='Credentials' color='mock' id={id} />
        <Box x={760} y={458} w={220} h={55} title='Systems Manager' subtitle='No-SSH access' color='mock' id={id} />
      </Part>
    </svg>
  )
}

export function TrustZonesDiagram() {
  const id = 'tz'
  const zones: { title: string; desc: string; detail: string; color: ColorKey; bg: string; border: string }[] = [
    { title: 'UNTRUSTED', desc: 'Internet — browser/mobile users, Epic HL7 over VPN', detail: 'No direct inbound path to compute', color: 'danger', bg: '#fef2f2', border: '#fca5a5' },
    { title: 'EDGE', desc: 'AWS managed — CloudFront (ACM), ALB (ACM cert)', detail: 'HTTPS only, TLS terminated here', color: 'db', bg: '#eff6ff', border: '#93c5fd' },
    { title: 'DMZ', desc: 'Public subnets — ALB across 2 AZs, NAT gateways', detail: 'No application code hosted in this tier', color: 'network', bg: '#fffbeb', border: '#fcd34d' },
    { title: 'TRUSTED', desc: 'Private app subnets — ECS Fargate, no public IP/SSH', detail: 'Secrets injected at task launch, no standing access', color: 'success', bg: '#f0fdf4', border: '#86efac' },
    { title: 'RESTRICTED', desc: 'Private data subnets — RDS PostgreSQL, encrypted at rest', detail: 'Multi-AZ in production, automated backups', color: 'auth', bg: '#fdf4ff', border: '#d8b4fe' },
  ]
  const zh = 100, gap = 18, sy = 50, w = 820
  const sgLabels = ['', 'TLS termination', 'SG: public :443', 'SG: ALB → ECS only', 'SG: ECS → RDS only']
  return (
    <svg viewBox='0 0 900 700' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}>
      <SvgDefs id={id} />
      <Label x={450} y={25} text='TRUST ZONES & SECURITY GROUP CHAIN' size={14} weight={700} color='#1e293b' />
      {zones.map((z, i) => {
        const y = sy + i * (zh + gap)
        const c = COLORS[z.color]
        return (
          <g key={z.title}>
            <Zone x={40} y={y} w={w} h={zh} title='' bgColor={z.bg} borderColor={z.border} />
            <Badge x={130} y={y + 22} text={z.title} color={c.stroke} />
            <Label x={60} y={y + 52} text={z.desc} size={12} weight={400} color='#334155' anchor='start' />
            <Label x={60} y={y + 74} text={z.detail} size={10} weight={400} color='#64748b' anchor='start' />
            {i < zones.length - 1 && (
              <>
                <Arr x1={450} y1={y + zh} x2={450} y2={y + zh + gap} id={id} />
                {sgLabels[i + 1] && <Badge x={700} y={y + zh + gap / 2} text={sgLabels[i + 1]} color={COLORS[zones[i + 1].color].stroke} />}
              </>
            )}
          </g>
        )
      })}
      <Label x={450} y={670} text='Each boundary is a security group — nothing reaches the database without passing both hops above it' size={10} weight={400} color='#64748b' />
    </svg>
  )
}

export function CiCdDiagram({ lit }: FlowProps = {}) {
  const id = 'cd'
  const lanes = [
    { y: 95, label: 'API DEPLOY', labelColor: '#e11d48', build: ['Docker build', 'Push to ECR'], deploy: ['ECS deploy', 'Rolling update'], color: 'api' as const },
    { y: 215, label: 'WEB DEPLOY', labelColor: '#7c3aed', build: ['pnpm build', 'Static assets'], deploy: ['S3 + CF invalidate', 'CDN cache cleared'], color: 'frontend' as const },
  ]
  return (
    <svg viewBox='0 0 1060 380' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}>
      <SvgDefs id={id} />
      <Label x={530} y={22} text='KEYLESS CI/CD PIPELINE' size={14} weight={700} color='#1e293b' />
      <Label x={530} y={44} text='GitHub Actions authenticates via OIDC — no stored AWS credentials' size={11} weight={400} color='#64748b' />

      {lanes.map(l => (
        <g key={l.label}>
          <Label x={100} y={l.y - 15} text={l.label} size={10} weight={700} color={l.labelColor} anchor='start' />
          <Part k='push' lit={lit}><Box x={20} y={l.y} w={195} h={62} title='Push to branch' subtitle='dev / staging / main' color='ci' id={id} /></Part>
          <Part k='gha' lit={lit}><Box x={260} y={l.y} w={195} h={62} title='GitHub Actions' subtitle='OIDC → IAM role' color='ci' id={id} /></Part>
          <Part k='build' lit={lit}><Box x={500} y={l.y} w={195} h={62} title={l.build[0]} subtitle={l.build[1]} color={l.color} id={id} /></Part>
          <Part k='deploy' lit={lit}><Box x={740} y={l.y} w={195} h={62} title={l.deploy[0]} subtitle={l.deploy[1]} color={l.color} id={id} /></Part>
          <Part k='push-gha' lit={lit}><Arr x1={215} y1={l.y + 31} x2={260} y2={l.y + 31} id={id} /></Part>
          <Part k='gha-build' lit={lit}><Arr x1={455} y1={l.y + 31} x2={500} y2={l.y + 31} id={id} /></Part>
          <Part k='build-deploy' lit={lit}><Arr x1={695} y1={l.y + 31} x2={740} y2={l.y + 31} id={id} /></Part>
        </g>
      ))}

      <Part k='breaker' lit={lit}><Badge x={838} y={170} text='Circuit breaker: auto-rollback' color='#e11d48' /></Part>
      <Label x={530} y={340} text='Independent paths — API and web deploys do not block each other' size={10} weight={400} color='#94a3b8' />
    </svg>
  )
}

export function ScalingDiagram() {
  const id = 'sc'
  return (
    <svg viewBox='0 0 1100 550' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}>
      <SvgDefs id={id} />
      <Label x={550} y={25} text='HORIZONTAL SCALABILITY' size={14} weight={700} color='#1e293b' />
      <Label x={550} y={48} text='Latency-based routing, non-sticky sessions, shared-nothing clusters' size={11} weight={400} color='#64748b' />

      <Box x={400} y={70} w={300} h={60} title='Amazon Route 53' subtitle='Latency-based routing · health checks' color='network' id={id} />

      {(['Region A · us-east-1', 'Region B · eu-central-1', 'Region C · us-west-2'] as const).map((r, i) => {
        const x = 30 + i * 360
        const y = 190
        return (
          <g key={r}>
            <Zone x={x} y={y} w={330} h={310} title={r} bgColor={i === 0 ? '#eff6ff' : '#f8fafc'} borderColor={i === 0 ? '#93c5fd' : '#cbd5e1'} />
            <Pill x={x + 30} y={y + 40} w={270} h={46} title='Application Load Balancer' subtitle='Non-sticky sessions' color='network' id={id} />
            <Box x={x + 40} y={y + 110} w={120} h={56} title='Instance 1' subtitle='NestJS' color='api' id={id} />
            <Box x={x + 175} y={y + 110} w={120} h={56} title='Instance N' subtitle='NestJS' color='api' id={id} />
            <Label x={x + 168} y={y + 138} text='…' size={18} weight={300} color='#94a3b8' />
            <Cyl x={x + 70} y={y + 200} w={190} h={72} title='PostgreSQL' subtitle='Multi-AZ · read replicas' color='db' id={id} />
            <Arr x1={550} y1={130} x2={x + 165} y2={y} id={id} />
          </g>
        )
      })}
      <Label x={550} y={530} text='Each tenant stays inside its own VPC — adding a site adds capacity to a cluster' size={10} weight={400} color='#94a3b8' />
    </svg>
  )
}

/** Re-order to received stock: the purchase order's states (z8nrz7f7dz). Steps in `PO_LIFECYCLE_FLOW`. */
export function PurchaseOrderLifecycleDiagram({ lit }: FlowProps = {}) {
  const id = 'po'
  const y = 150
  const h = 76
  return (
    <svg viewBox='0 0 1100 420' xmlns='http://www.w3.org/2000/svg' style={{ width: '100%', height: 'auto' }}>
      <SvgDefs id={id} />
      <Part k='due' lit={lit}><Box x={20} y={y} w={150} h={h} title='Material due' subtitle='Re-Order tab · red PO icon' color='danger' id={id} /></Part>
      <Part k='calc' lit={lit}><Box x={200} y={y} w={150} h={h} title='Re-order calculation' subtitle='Settings · quantity' color='ci' id={id} /></Part>
      <Part k='draft' lit={lit}><Box x={380} y={y} w={150} h={h} title='Draft' subtitle='Free edits · add lines' color='neutral' id={id} /></Part>
      <Part k='sent' lit={lit}><Box x={560} y={y} w={150} h={h} title='Sent' subtitle='Edits need a reason' color='db' id={id} /></Part>
      <Part k='received' lit={lit}><Box x={740} y={y} w={150} h={h} title='Received' subtitle='Read-only' color='success' id={id} /></Part>
      <Part k='lot' lit={lit}><Box x={920} y={y} w={160} h={h} title='Add lot' subtitle='Stock booked in' color='partner' id={id} /></Part>
      <Part k='cancelled' lit={lit}><Box x={470} y={320} w={150} h={60} title='Cancelled' subtitle='With a reason · read-only' color='mock' id={id} /></Part>

      <Part k='due-calc' lit={lit}><Arr x1={170} y1={y + h / 2} x2={200} y2={y + h / 2} id={id} /></Part>
      <Part k='calc-draft' lit={lit}><Arr x1={350} y1={y + h / 2} x2={380} y2={y + h / 2} id={id} /></Part>
      <Part k='draft-sent' lit={lit}><Arr x1={530} y1={y + h / 2} x2={560} y2={y + h / 2} id={id} /></Part>
      <Part k='sent-received' lit={lit}><Arr x1={710} y1={y + h / 2} x2={740} y2={y + h / 2} id={id} /></Part>
      <Part k='received-lot' lit={lit}><Arr x1={890} y1={y + h / 2} x2={920} y2={y + h / 2} id={id} /></Part>
      <Part k='cancel-draft' lit={lit}><Arr x1={455} y1={y + h} x2={515} y2={320} dashed id={id} /></Part>
      <Part k='cancel-sent' lit={lit}><Arr x1={635} y1={y + h} x2={575} y2={320} dashed id={id} /></Part>

      <Part k='revise' lit={lit}>
        <Pill x={545} y={28} w={180} h={46} title='Edit with a reason' subtitle='becomes Rev n + 1' color='network' id={id} />
        <Arr x1={615} y1={74} x2={615} y2={y} dashed id={id} />
        <Arr x1={655} y1={y} x2={655} y2={74} dashed id={id} />
      </Part>
      <Part k='on-order' lit={lit}><Badge x={420} y={118} text='Draft + Sent = on order' color='#b45309' /></Part>
      <Part k='snapshot' lit={lit}><Badge x={730} y={262} text='Template frozen at send' color='#2563eb' /></Part>

      <Label x={550} y={406} text='Mark as sent · Mark received · Cancel order — from the purchase order dialog' size={10} weight={400} color='#94a3b8' />
    </svg>
  )
}
